import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Repeat } from 'lucide-react';

import { useGetVoiceScanImage } from 'services/voice-scan/hooks';
import VoiceRecorder from './VoiceRecorder';
import AudioReview from './AudioReview';
import AudioReportGenerating from './ReportGenerating';
import { useVoiceScanStore } from 'store/voice-scan';
import { Skeleton } from 'components/ui/skeleton';
import ChangeVoiceScanImageDialog from 'components/dialogs/ChangeVoiceImage';
import { cn } from 'lib/utils';

export default function StartScan() {
  const queryClient = useQueryClient();
  const resetVoiceStore = useVoiceScanStore((store) => store.resetStore);

  const [isCompleted, setIsCompleted] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReset, setIsReset] = useState(false);

  const onReset = () => {
    setIsCompleted(false);
    setIsGenerating(false);
    setIsReset(true);
    resetVoiceStore();
    queryClient.invalidateQueries({ queryKey: ['voice-scan-image'] });
  };

  if (isGenerating) {
    return <AudioReportGenerating onReset={onReset} />;
  }

  return (
    <>
      <div
        className={cn(
          'grid items-center justify-center h-[calc(100vh-12rem)] lg:h-[calc(100vh-8rem)] max-w-xl mx-auto mb-8',
          isCompleted ? 'hidden' : 'block'
        )}
      >
        <ImageViewer />

        <VoiceRecorder setIsCompleted={setIsCompleted} isReset={isReset} />
      </div>
      <div className={cn(isCompleted ? 'block' : 'hidden')}>
        <AudioReview
          onSubmit={() => {
            setIsGenerating(true);
            setIsReset(false);
          }}
          onRetry={onReset}
        />
      </div>
    </>
  );
}

function ImageViewer() {
  const { t } = useTranslation();
  const { setImageData, isRecording } = useVoiceScanStore();

  const { data: imageData, isFetching } = useGetVoiceScanImage();

  const [imageLoaded, setImageLoaded] = useState(false);
  const [changeImage, setChangeImage] = useState(false);

  useEffect(() => {
    if (imageData?.image_id) {
      setImageData(imageData);
    }
  }, [imageData]);

  return (
    <>
      <div className='grid h-full pt-12 mx-4 lg:h-fit place-content-center'>
        <h1 className='text-lg font-semibold leading-tight text-center md:px-8 lg:text-xl'>
          {t('voice_scan_title')}
        </h1>
        <p className='mt-2 text-center'>{t('voice_scan_info')}</p>

        <div className='relative mx-auto mt-6 overflow-hidden h-72 aspect-square rounded-3xl'>
          {(!imageLoaded || isFetching) && (
            <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/70'>
              <Skeleton className='absolute z-50 w-full h-full' />
            </div>
          )}

          {!isFetching && imageData?.image_id && (
            <img
              crossOrigin='anonymous'
              src={imageData?.image_url}
              alt={imageData?.image_id}
              className='object-contain w-full h-full'
              onLoad={() => setImageLoaded(true)}
            />
          )}

          {!isRecording && imageLoaded && !isFetching ? (
            <button
              className='absolute right-4 bottom-3 bg-[#0206177A] text-white rounded-[10px] px-3 py-1 text-sm flex items-center gap-2'
              onClick={() => setChangeImage(true)}
            >
              <span>{t('change_image')}</span>
              <Repeat color='#fff' size={16} />
            </button>
          ) : null}
        </div>
      </div>

      <ChangeVoiceScanImageDialog
        open={changeImage}
        onClose={() => setChangeImage(false)}
      />
    </>
  );
}
